import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS
} from '../../models/pagination.model';

/**
 * Entrada del listado de páginas a renderizar.
 * Un número significa "botón de página N"; el literal 'ellipsis' indica un
 * salto (…) para representar páginas ocultas.
 */
export type PageEntry = number | 'ellipsis';

/**
 * Paginador reutilizable Cyber-Minimalist.
 *
 * Patrón "controlado": el componente sólo emite eventos; el padre mantiene el
 * estado (page, pageSize, total) y reacciona. No mantiene estado interno propio
 * más allá del cálculo derivado (totalPages, listado de entradas).
 *
 * Layout (Opción A acordada):
 *   [Mostrar 25 ▾]                Mostrando 1–25 de 137 · ‹ 1 2 … 6 ›
 *
 * Outputs SEPARADOS (acordado):
 *   - `pageChange`     → cuando el usuario hace click en un control de página.
 *   - `pageSizeChange` → cuando el usuario cambia el dropdown de tamaño.
 *     Al cambiar el tamaño NO se emite pageChange; el padre decide qué hacer
 *     con la página (típicamente: resetear a 1).
 *
 * @version: 19/05/2026_1
 */
@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginatorComponent implements OnChanges {

  /** Total de items del recurso. Obligatorio. */
  @Input({ required: true }) total!: number;

  /** Página actual (1-indexada). */
  @Input() page: number = 1;

  /** Tamaño de página actual. */
  @Input() pageSize: number = DEFAULT_PAGE_SIZE;

  /** Opciones disponibles en el selector de tamaño. */
  @Input() pageSizeOptions: readonly number[] = DEFAULT_PAGE_SIZE_OPTIONS;

  /** Deshabilita todo el paginador (típicamente durante una carga). */
  @Input() disabled: boolean = false;

  /** Etiqueta de accesibilidad para el <nav>. */
  @Input() ariaLabel: string = 'Paginación';

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  /** Valores derivados (recalculados en ngOnChanges, NO en getters). */
  totalPages: number = 0;
  firstItemIndex: number = 0;
  lastItemIndex: number = 0;
  pages: PageEntry[] = [];

  /**
   * ID único por instancia para vincular <label for> ↔ <select id>.
   * Resuelve el caso de tener más de un paginador en la misma página sin
   * colisión de IDs (lo cual rompe la accesibilidad).
   */
  readonly uniqueId: string = Math.random().toString(36).slice(2, 9);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['total'] || changes['page'] || changes['pageSize']) {
      this.recalculate();
    }
  }

  /** Navega a la página indicada, clamped al rango [1, totalPages]. */
  goTo(targetPage: number): void {
    if (this.disabled) return;
    const clamped = Math.max(1, Math.min(this.totalPages, targetPage));
    if (clamped === this.page || this.totalPages === 0) return;
    this.pageChange.emit(clamped);
  }

  /** Handler del <select> de tamaño de página. */
  onPageSizeChange(event: Event): void {
    const raw = (event.target as HTMLSelectElement).value;
    const newSize = parseInt(raw, 10);
    if (Number.isNaN(newSize) || newSize === this.pageSize) return;
    this.pageSizeChange.emit(newSize);
  }

  /** trackBy del *ngFor sobre `pages`, estabiliza el DOM entre re-renders. */
  trackByEntry(index: number, entry: PageEntry): string | number {
    return typeof entry === 'number' ? entry : `ellipsis-${index}`;
  }

  /** Helper para discriminar tipos en el template sin casts. */
  isEllipsis(entry: PageEntry): boolean {
    return entry === 'ellipsis';
  }

  /** Conversión segura PageEntry → number (solo para entradas numéricas). */
  toNumber(entry: PageEntry): number {
    return typeof entry === 'number' ? entry : 0;
  }

  private recalculate(): void {
    const safeTotal = Math.max(0, this.total || 0);
    const safeSize = Math.max(1, this.pageSize || DEFAULT_PAGE_SIZE);

    this.totalPages = safeTotal > 0 ? Math.ceil(safeTotal / safeSize) : 0;
    this.firstItemIndex = safeTotal > 0 ? (this.page - 1) * safeSize + 1 : 0;
    this.lastItemIndex = Math.min(this.page * safeSize, safeTotal);
    this.pages = this.buildPageList();
  }

  /**
   * Sliding window simple. Máximo 7 botones de página visibles + hasta 2
   * elipsis. Tres casos:
   *  - total ≤ 7         → todas.
   *  - current ≤ 4       → [1 2 3 4 5 … last]
   *  - current ≥ total-3 → [1 … last-4 last-3 last-2 last-1 last]
   *  - en el medio       → [1 … current-1 current current+1 … last]
   */
  private buildPageList(): PageEntry[] {
    const total = this.totalPages;
    if (total <= 0) return [];
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const current = Math.max(1, Math.min(total, this.page));
    const out: PageEntry[] = [];

    if (current <= 4) {
      for (let i = 1; i <= 5; i++) out.push(i);
      out.push('ellipsis');
      out.push(total);
      return out;
    }

    if (current >= total - 3) {
      out.push(1);
      out.push('ellipsis');
      for (let i = total - 4; i <= total; i++) out.push(i);
      return out;
    }

    out.push(1);
    out.push('ellipsis');
    for (let i = current - 1; i <= current + 1; i++) out.push(i);
    out.push('ellipsis');
    out.push(total);
    return out;
  }
}